import { createAsyncThunk } from "@reduxjs/toolkit"

import { errorSave } from "../../../redux/errors.slice"
import { type RootState } from "../../../store.ts"
import {
  type BaseApiResponse,
  type BasePagination
} from "../../../types/api.ts"
import { showResponseMessage } from "../../../utils/handlers/show-response-message.ts"
import { selectActiveBranchId } from "../../Branches/redux/branches.slice.ts"
import * as services from "../api/services"
import type {
  CreateSupplierData,
  UpdateSupplierData
} from "../api/services.types.ts"
import type { SupplierListItem } from "../types/types.ts"
import { suppliersSliceName } from "./suppliers.constants.ts"
import type { SuppliersFilters } from "./suppliers.types.ts"

export const fetchSuppliers = createAsyncThunk<
  BasePagination<SupplierListItem>,
  Partial<SuppliersFilters> & { isMore?: boolean }
>(
  `${suppliersSliceName}/fetchSuppliers`,
  // `isMore` tells the reducer whether to append; it is not a query parameter.
  async ({ isMore: _isMore, ...params }, { dispatch, getState, rejectWithValue }) => {
    // REOPENED branch-isolation audit: Suppliers had no branch param at all
    // — the directory showed company-wide data under every workspace. The
    // server still independently re-validates this against the caller's
    // real accessible teams (`apps.user.branch_scope`), never trusting the
    // selector alone.
    const activeBranchId = selectActiveBranchId(getState() as RootState)
    const response = await services.getSuppliers({
      ...params,
      ...(activeBranchId ? { team: activeBranchId } : {})
    })

    if (!response.ok) {
      dispatch(errorSave(response?.data?.details))
      return rejectWithValue(response.data)
    }

    return response.data as BasePagination<SupplierListItem>
  }
)

export const createSupplier = createAsyncThunk<
  BaseApiResponse<SupplierListItem>,
  CreateSupplierData
>(
  `${suppliersSliceName}/createSupplier`,
  async (data, { dispatch, rejectWithValue }) => {
    const response = await services.createSupplier(data)

    if (!response.ok) {
      dispatch(errorSave(response?.data?.details))
      return rejectWithValue(response.data)
    }

    showResponseMessage(response, "Supplier created successfully.")
    return response.data as BaseApiResponse<SupplierListItem>
  }
)

export const updateSupplier = createAsyncThunk<
  BaseApiResponse<SupplierListItem>,
  UpdateSupplierData
>(
  `${suppliersSliceName}/updateSupplier`,
  async (data, { dispatch, rejectWithValue }) => {
    const response = await services.updateSupplier(data)

    if (!response.ok) {
      dispatch(errorSave(response?.data?.details))
      return rejectWithValue(response.data)
    }

    showResponseMessage(response, "Supplier updated successfully.")
    return response.data as BaseApiResponse<SupplierListItem>
  }
)

export const deleteSupplier = createAsyncThunk<
  BaseApiResponse<null> & { id: string },
  string
>(
  `${suppliersSliceName}/deleteSupplier`,
  async (supplierId, { dispatch, rejectWithValue }) => {
    const response = await services.deleteSupplier(supplierId)

    if (!response.ok) {
      dispatch(errorSave(response?.data?.details))
      return rejectWithValue(response.data)
    }

    showResponseMessage(response, "Supplier deleted successfully.")
    return { ...response.data, id: supplierId } as BaseApiResponse<null> & {
      id: string
    }
  }
)

export const fetchSupplierDetails = createAsyncThunk<SupplierListItem, string>(
  `${suppliersSliceName}/fetchSupplierDetails`,
  async (supplierId, { dispatch, rejectWithValue }) => {
    const response = await services.getSupplierDetails(supplierId)

    if (!response.ok) {
      dispatch(errorSave(response?.data?.details))
      return rejectWithValue(response.data)
    }

    return response.data as SupplierListItem
  }
)
